import React, { useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import Tooltip from "@mui/material/Tooltip";
import {
  deleteNotificationById,
  getNotifications,
  sendNotification,
} from "../../Services/api";
import {
  CircularProgress,
  Box,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";

const Notifications = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [notificationToSend, setNotificationToSend] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getNotificationsData();
  }, []);

  const getNotificationsData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getNotifications();
      setData(result?.notifications || []);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      setError("Failed to load notifications. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddBtn = () => {
    navigate("/notification-create");
  };

  const handleEdit = (id) => {
    navigate(`/notification-edit/${id}`);
  };

  const handleDeleteClick = (notification) => {
    setNotificationToDelete(notification);
    setDeleteDialogOpen(true);
  };

  const handleSendClick = (notification) => {
    setNotificationToSend(notification);
    setSendDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await deleteNotificationById(notificationToDelete._id);
      setDeleteDialogOpen(false);
      getNotificationsData();
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      setError("Failed to load notifications. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendConfirm = async () => {
    const params = {
      notificationId: notificationToSend._id,
    };
    try {
      setLoading(true);
      setError(null);
      const result = await sendNotification(params);
      console.log("result", result);
      setSendDialogOpen(false);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      setError("Failed to load notifications. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setNotificationToDelete(null);
  };

  const handleSendCancel = () => {
    setSendDialogOpen(false);
    setNotificationToSend(null);
  };

  const columns = [
    {
      name: "title",
      label: "Title",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "message",
      label: "Message",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "imageUrl",
      label: "Image",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) =>
          value ? (
            <Avatar
              alt="Notification"
              src={process.env.REACT_APP_API_BASE_URL + value}
              variant="square"
              sx={{ width: 56, height: 56 }}
            />
          ) : (
            <Avatar
              variant="square"
              sx={{ width: 56, height: 56, bgcolor: "grey.300" }}
            >
              No Image
            </Avatar>
          ),
      },
    },
    {
      name: "_id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const notification = data[tableMeta.rowIndex];
          return (
            <div>
              <Tooltip title="Delete">
                <IconButton onClick={() => handleDeleteClick(notification)}>
                  <DeleteIcon color="error" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Send Notification">
                <IconButton onClick={() => handleSendClick(notification)}>
                  <SendIcon color="primary" />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      },
    },
  ];

  const options = {
    filterType: "checkbox",
    responsive: "standard",
    print: false,
    download: false,
    viewColumns: false,
    filter: true,
    selectableRows: "none",
    customToolbar: () => (
      <Tooltip title="Add Notification">
        <IconButton onClick={handleAddBtn}>
          <ControlPointIcon color="primary" />
        </IconButton>
      </Tooltip>
    ),
    textLabels: {
      body: {
        noMatch: loading ? (
          <CircularProgress size={24} />
        ) : error ? (
          error
        ) : (
          "Sorry, there is no matching data to display"
        ),
      },
    },
  };

  if (loading && data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        {error}
      </Box>
    );
  }

  return (
    <>
      <MUIDataTable
        title={"All Notifications"}
        data={data}
        columns={columns}
        options={options}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the notification "
          {notificationToDelete?.title}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Notification Confirmation Dialog */}
      <Dialog open={sendDialogOpen} onClose={handleSendCancel}>
        <DialogTitle>Confirm Send Notification</DialogTitle>
        <DialogContent>
          Are you sure you want to send push notification "
          {notificationToSend?.title}" to all users?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSendCancel}>Cancel</Button>
          <Button
            onClick={handleSendConfirm}
            color="primary"
            variant="contained"
            startIcon={<SendIcon />}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Notifications;
